/**
 * Chart documentation shown behind the info icon on the Meta Summary dashboard.
 *
 * Keys match the chart and table link property names in
 * `MopaMetaSummaryComponent`.
 */
export const MopaMetaSummaryChartDocs = {
    dau: /* HTML */ `<p>
            Active users per day, split into the META / HOPA / CORE LOOP / META
            Prod. populations.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - players, one line per
            population.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code> per
            day in each population. <strong>All</strong> = active that day;
            <strong>META</strong> = opened the deco menu;
            <strong>HOPA</strong> = spent at least 1 energy;
            <strong>META Prod.</strong> = placed at least 1 decoration,
            move/redo excluded; <strong>CORE LOOP</strong> = HOPA and META on
            the same day. The checkboxes pick which lines are drawn.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,

    median_new_decos_placed_per_active_user_by_date: /* HTML */ `<p>
            Median decorations placed per active user in the selected date or
            period.
        </p>
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - decorations per active user.
        </p>
        <p>
            <strong>What's counted:</strong> first-ever placements only,
            re-placements excluded. Each player's placements are summed over the
            period first, so the unit stays the player under date aggregation;
            players who placed nothing count as 0 and pull the median down. Same
            definition as the KPI table.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,

    average_new_decos_placed_per_active_user_by_date: /* HTML */ `<p>
            Average decorations placed per active user in the selected date or
            period.
        </p>
        <p>
            <strong>Axes:</strong> X - date, bucketed by
            <strong>Date aggregation</strong>; Y - decorations per active user.
        </p>
        <p>
            <strong>What's counted:</strong> first-ever placements only,
            re-placements excluded. Each player's placements are summed over the
            period first, so the unit stays the player under date aggregation;
            players who placed nothing count as 0 and pull the average down.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,

    median_new_decos_placed_per_active_user_by_day_no: /* HTML */ `<p>
            Median decorations placed per active user at each day number.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day, cut at the "Limit max day no" setting; Y - decorations
            per active user.
        </p>
        <p>
            <strong>What's counted:</strong> first-ever placements only, across
            users active at that day number; players who placed nothing count as
            0.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,

    average_new_decos_placed_per_active_user_by_day_no: /* HTML */ `<p>
            Average decorations placed per active user at each day number.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day, cut at the "Limit max day no" setting; Y - decorations
            per active user.
        </p>
        <p>
            <strong>What's counted:</strong> first-ever placements only, across
            users active at that day number; players who placed nothing count as
            0.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,

    kpi_summary_table: /* HTML */ `<p>
            META and HOPA KPIs per date or period, newest first.
        </p>
        <p>
            Under date aggregation the unit stays <strong>the player</strong>:
            each player's activity is summed over the period first, then medians
            and shares are computed across players. Players without the measured
            activity count as <strong>0</strong> and pull the median down; "by
            HOPA Users" means only that population is counted. Conversion Rate,
            ARPDAU and ARPPDAU are instead weighted daily averages - sums of
            daily payers and daily active users, as on the Sales dashboard - so
            their denominator is player-days, not the distinct players in the
            Active Users column.
        </p>
        <ul>
            <li>
                <strong>Active Users</strong> - distinct active players; becomes
                WAU/MAU under aggregation.
            </li>
            <li>
                <strong>HOPA Users Share (%)</strong> - players who spent at
                least 1 energy / Active Users.
            </li>
            <li>
                <strong>META Users Share (%)</strong> - players who opened the
                deco menu (DesignModeFlowOpened) / Active Users.
            </li>
            <li>
                <strong>META Prod. Users Share (%)</strong> - players who placed
                at least 1 decoration, excluding move/redo (same deco removed
                within the prior 24h) / Active Users.
            </li>
            <li>
                <strong>CORE LOOP Users Share (%)</strong> - players who were
                HOPA and META on the same day / Active Users.
            </li>
            <li>
                <strong>Median New Decos Placed per active user</strong> -
                median of first-ever placements (PlacementNumber = 1) per
                player; no placements counts as 0.
            </li>
            <li>
                <strong>Median Energy Spent by HOPA Users</strong> - median of
                each player's energy outflow in the period, over players who
                spent any energy. Empty when a filter or split is not supported
                by the resources table.
            </li>
            <li>
                <strong>Median Time In App [min]</strong> - median of each
                player's total session minutes.
            </li>
            <li>
                <strong>Median META Time per active user [min]</strong> - median
                of each player's decorating minutes (closed DesignMode flows); no
                META time counts as 0, and pre-4.0.100 builds do not report it,
                so they count as 0 too.
            </li>
            <li>
                <strong>Median META Time Share (%)</strong> - each player's META
                time over their client-reported play time; the median runs over
                <strong>measured players only</strong>, players without Player
                Context data drop out.
            </li>
            <li>
                <strong>Median HOPA Time per active user [min]</strong> - HOPA
                time is the <strong>remainder</strong> of client-reported play
                time after decorating and shop browsing (client total - META -
                Shop), not a measured counter. Measured players only: pre-4.0.100
                builds report no total, so they are excluded rather than counted
                as 0.
            </li>
            <li>
                <strong>Median HOPA Time Share (%)</strong> - as META Time Share,
                with that remainder on top. META + Shop + HOPA share = 100% of
                every player's day; Shop is not reported before client 4.1.100,
                so until then shop minutes count as 0 and land inside HOPA.
            </li>
            <li>
                <strong>Conversion Rate</strong> - sum of daily payers (at least
                1 transaction that day) / sum of daily active users.
            </li>
            <li>
                <strong>ARPDAU</strong> - net revenue (inapp + ad) / sum of daily
                active users.
            </li>
            <li>
                <strong>ARPPDAU</strong> - net revenue / sum of daily payers.
            </li>
            <li>
                <strong>Revenue</strong> - net revenue (inapp + ad) in the
                selected currency.
            </li>
        </ul>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code> (one row per
            player and active day); <code>bi_viz.dashboard_resources</code>
            (energy); valid players only.
        </p>`,
} as const;
